// The write layer for a TRUE RESTART of a reporting cycle, and its read-only preview.
// Client half of 0003q.
//
// NOT THE SAME THING AS A REOPEN, and the difference is the whole point.
// reopen_reporting_cycle (0003p) RESUMES: it writes four columns and deliberately does not
// touch current_stage, stage_outputs, ir or the reconciliation ledger. Every completion marker
// survives, which is correct when nothing about the inputs changed and wrong when the code or
// the prompt was the bug. On 2026-08-17 a resume of cycle ec8e622a after a real prompt and
// compute fix produced byte-identical prose, because research-report-narrative treats any
// truthy value in stage_outputs.narrative as "this group is done" (index.ts:719-722) and the
// resume preserved all five of them.
//
// restart_reporting_cycle rewinds the cursor to a NAMED stage and clears the markers behind it
// so those stages actually re-derive. Depths, shallowest first:
//   render     re-render only. The IR does not move, so frozen versions stay gate-bindable.
//   narrative  re-author every prose slot, re-assert the ledger, re-render.
//   verify     identical in effect to narrative today (verify's only marker is one of the
//              three narrative depth already clears). The name is kept so the ladder reads as
//              the stage list.
//   compute    re-derive the numbers against the SAME frozen inputs. snapshot_ref is kept.
//   snapshot   re-freeze the inputs. Honest consequence: a new snapshot freezes whatever is
//              attested at the moment it runs, so it is NOT a reproduction of the earlier set.
//
// EVERY REFUSAL AND EVERY FACT IS THE SERVER'S. The dialog is not allowed to hand-write what a
// restart discards, because a hand-written sentence is exactly how the 2026-08-17 outcome came
// to be surprising: the Reopen dialog says completed work is not thrown away, which is true and
// is the trap. restart_reporting_cycle_preview returns the depth plan, the stage_outputs keys
// and ir subtrees that will actually clear ON THIS ROW, the reconciliation row count, the frozen
// artifact list, the restart ordinal and remaining budget, the stage_seq headroom against this
// cycle's own measured walk cost, the tier-2 acknowledgement string when one is needed, and
// every refusal that currently holds. The dialog renders that and nothing else.
//
// RESTARTING DOES NOT RUN ANYTHING. 0003q keeps 0003p's separation of the CONTROL act from the
// SPEND act. Queueing the run stays a second, explicit decision.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** Mirrors the RPC's own minimum. The server re-checks; this only shapes the UI. */
export const RESTART_REASON_MIN_CHARS = 20;

/** The depth ladder, shallowest first. Must match reporting_restart_plan()'s CASE arms. */
export const RESTART_DEPTHS = ["render", "narrative", "verify", "compute", "snapshot"] as const;
export type RestartDepth = (typeof RESTART_DEPTHS)[number];

export interface RestartPlan {
  from_stage: string;
  target_stage: string;
  new_status: string;
  clears_stage_outputs: string[];
  clears_ir: string[];
  clears_snapshot_ref: boolean;
  wipes_stage_outputs: boolean;
  wipes_ir: boolean;
  deletes_reconciliations: boolean;
  moves_ir: boolean;
  note: string;
}

export interface RestartRefusal {
  tier: 1 | 2 | 3;
  code: string;
  detail: string;
}

export interface RestartArtifactVersion {
  kind: string;
  version_id: string;
  artifact_sha256: string;
  ir_sha256: string | null;
  storage_path: string;
  bound_to_current_ir: boolean;
}

export interface RestartPreview {
  cycle_id: string;
  plan: RestartPlan;
  refusals: RestartRefusal[];
  can_restart: boolean;
  status: string;
  current_stage: string;
  stage_seq: number;
  stage_cap: number;
  stage_seq_headroom: number;
  /** This cycle's last walk, measured from the previous control row. NULL if never measured. */
  walk_cost_measured: number | null;
  walk_cost_required: number;
  tick_count: number;
  reopen_seq: number;
  last_error: string | null;
  controls_used: number;
  controls_budget: number;
  restart_ordinal: number;
  ir_sha256: string | null;
  will_clear: {
    stage_outputs_keys: string[];
    ir_subtrees: string[];
    snapshot_ref: boolean;
    reconciliation_rows: number;
    last_error: boolean;
    tick_count_reset_to: number;
  };
  preserved: Record<string, string>;
  artifact_versions: RestartArtifactVersion[];
  ack_required: boolean;
  ack_string: string | null;
}

export interface RestartResult {
  restarted: boolean;
  cycle_id: string;
  mode: string;
  depth: string;
  plan: RestartPlan;
  prior_status: string;
  prior_current_stage: string;
  status: string;
  current_stage: string;
  stage_seq: number;
  tick_count: number;
  reopen_seq: number;
  restart_ordinal: number;
  control_budget: number;
  cleared: {
    stage_outputs_keys: string[];
    ir_subtrees: string[];
    snapshot_ref: boolean;
    reconciliation_rows: number;
  };
  stranded_artifacts: RestartArtifactVersion[];
  next_step?: string;
  [k: string]: unknown;
}

/**
 * READ-ONLY. Every sentence the confirm dialog states comes from here.
 * Disabled until a depth is chosen, and never cached across depths: the plan, the resolved
 * clear list and the acknowledgement string are all depth-specific.
 */
export function useRestartReportingCyclePreview(
  cycleId: string,
  depth: RestartDepth | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["restart_reporting_cycle_preview", cycleId, depth],
    enabled: Boolean(cycleId) && Boolean(depth) && enabled,
    // The refusals include an in-flight run job and the control budget, both of which can
    // change under the operator while the dialog is open. Never serve a stale refusal set.
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("restart_reporting_cycle_preview", {
        _cycle_id: cycleId,
        _from_stage: depth,
      });
      if (error) throw error;
      return data as RestartPreview;
    },
  });
}

export function useRestartReportingCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cycleId,
      depth,
      reason,
      ack,
    }: {
      cycleId: string;
      depth: RestartDepth;
      reason: string;
      /** The echoed server-computed acknowledgement. Sent as null when none is required. */
      ack?: string | null;
    }) => {
      const { data, error } = await (supabase as any).rpc("restart_reporting_cycle", {
        _cycle_id: cycleId,
        _from_stage: depth,
        _reason: reason,
        _ack: ack ?? null,
      });
      if (error) throw error;
      return data as RestartResult;
    },
    onSuccess: (_data, vars) => {
      // Same set as useReopenReportingCycle plus the artifacts query: a restart at an
      // IR-moving depth strands every frozen version bound to the old IR, so a cached
      // artifact panel would keep showing rows the gate can no longer bind.
      qc.invalidateQueries({ queryKey: ["get_reporting_cycle", vars.cycleId] });
      qc.invalidateQueries({ queryKey: ["list_reporting_cycles"] });
      qc.invalidateQueries({ queryKey: ["get_reporting_cycle_run", vars.cycleId] });
      qc.invalidateQueries({ queryKey: ["list_reporting_artifacts", vars.cycleId] });
      qc.invalidateQueries({ queryKey: ["restart_reporting_cycle_preview", vars.cycleId] });
    },
  });
}
